import { hashKey, QueryClient, QueryKey } from '@tanstack/react-query'
import { financialStatementsKeys } from '../../features/financialStatements/queries/financialStatementsQueryKeys'
import customReportQueryKeys from '../../features/customReport/queries/customReportQueryKeys'
import { accountCalculationQueryKeys } from '../../features/accountCalculation/queries/accountCalculationQueryKeys'
import { investmentQueryKeys } from '../../features/investment/queries/investmentQueryKeys'
import loanQueryKeys from '../../features/loan/queries/loanQueryKeys'
import { amendmentKeys } from '../../features/amendment/queries/amendmentQueryKeys'
import { budgetCommentsKeys } from '../../features/budgetComments/queries/budgetCommentsQueryKeys'
import driverDataQueryKeys from '../../features/driverData/queries/driverDataQueryKeys'
import budgetDriversQueryKeys from '../../features/budgetDrivers/queries/budgetDriversQueryKeys'
import formulaQueryKeys from '../../features/formulas/queries/formulaQueryKeys'
import { deprecationPlanQueryKeys } from '../../features/deprecationPlan/queries/deprecationPlanQueryKeys'
import accountQueryKeys from '../../features/account/queries/accountQueryKeys'
import personnelQueryKeys from '../../features/personnel/queries/personnelQueryKeys'
import exchangeRateQueryKeys from '../../features/exchangeRate/queries/exchangeRateQueryKeys'
import accountMapQueryKeys from '../../features/accountMap/queries/accountMapQueryKeys'
import voucherQueryKeys from '../../features/vouchers/queries/voucherQueryKeys'
import fiscalYearQueryKeys from '../../features/fiscalYear/queries/fiscalYearQueryKeys'
import dimensionQueryKeys from '../../features/dimension/queries/dimensionQueryKeys'
import companyGroupSettingQueryKeys from '../../features/companyGroup/queries/companyGroupSetting/companyGroupSettingQueryKeys'
import companyGroupQueryKeys from '../../features/companyGroup/queries/companyGroupQueryKeys'
import budgetingScenarioQueryKeys from '../../features/scenario/queries/budgetingScenario/budgetingScenarioQueryKeys'
import forecastQueryKeys from '../../features/scenario/queries/forecast/forecastQueryKeys'
import reallocationQueryKeys from '../../features/reallocation/queries/reallocationQueryKeys'

// Single source of truth for cache invalidation: entity key → affected query keys.
// The entity key is used as the mutationKey of every mutation that writes the entity,
// and Novu events (sent to the saving user when a 202 background recalculation finishes)
// are mapped to the same entity keys below.
// refetch: dependent queries, refetched if active and marked stale if inactive
// reports: heavy report queries, see applyInvalidation for how they are handled

export type CacheInvalidation = {
  refetch: ReadonlyArray<QueryKey>
  reports: ReadonlyArray<QueryKey>
}

export const reportQueryKeys: ReadonlyArray<QueryKey> = [financialStatementsKeys.all, customReportQueryKeys.sections()]

export const depreciationCorrectionKey = [{ scope: 'depreciationCorrection' }] as const

const withReports = (...refetch: QueryKey[]): CacheInvalidation => ({ refetch, reports: reportQueryKeys })

// A forecast is a budgeting scenario (separate endpoint only), so both invalidate the same scenario-scoped data
const scenarioInvalidation = withReports(
  budgetingScenarioQueryKeys.all,
  forecastQueryKeys.all,
  companyGroupSettingQueryKeys.all,
  amendmentKeys.all,
  budgetCommentsKeys.all,
  deprecationPlanQueryKeys.all,
  investmentQueryKeys.all,
  loanQueryKeys.all
)

const dependencies: ReadonlyArray<{ key: QueryKey; invalidation: CacheInvalidation }> = [
  { key: accountCalculationQueryKeys.all, invalidation: withReports(accountCalculationQueryKeys.all) },
  { key: investmentQueryKeys.all, invalidation: withReports(investmentQueryKeys.all, loanQueryKeys.all) },
  { key: amendmentKeys.all, invalidation: withReports(amendmentKeys.all) },
  { key: driverDataQueryKeys.all, invalidation: withReports(driverDataQueryKeys.all) },
  { key: budgetDriversQueryKeys.all, invalidation: withReports(budgetDriversQueryKeys.all) },
  { key: formulaQueryKeys.all, invalidation: withReports(formulaQueryKeys.all) },
  { key: loanQueryKeys.all, invalidation: withReports(loanQueryKeys.all) },
  // Instalment corrections are edited cell by cell in LoanRepaymentSceduleModal: only the schedule
  // refreshes here, loans and reports are refreshed via loanQueryKeys.all when the modal closes
  {
    key: loanQueryKeys.allInstalments(),
    invalidation: { refetch: [loanQueryKeys.allInstalments()], reports: [] }
  },
  { key: deprecationPlanQueryKeys.all, invalidation: withReports(deprecationPlanQueryKeys.all) },
  { key: accountQueryKeys.all, invalidation: withReports(accountQueryKeys.all) },
  { key: depreciationCorrectionKey, invalidation: withReports(depreciationCorrectionKey, investmentQueryKeys.all) },
  { key: personnelQueryKeys.all, invalidation: withReports(personnelQueryKeys.all) },
  { key: exchangeRateQueryKeys.all, invalidation: withReports(exchangeRateQueryKeys.all) },
  { key: accountMapQueryKeys.all, invalidation: withReports(accountMapQueryKeys.all) },
  { key: voucherQueryKeys.all, invalidation: withReports(voucherQueryKeys.all) },
  { key: fiscalYearQueryKeys.all, invalidation: withReports(fiscalYearQueryKeys.all) },
  { key: dimensionQueryKeys.all, invalidation: withReports(dimensionQueryKeys.all) },
  {
    key: companyGroupSettingQueryKeys.all,
    invalidation: withReports(companyGroupSettingQueryKeys.all, exchangeRateQueryKeys.all)
  },
  { key: budgetingScenarioQueryKeys.all, invalidation: scenarioInvalidation },
  { key: forecastQueryKeys.all, invalidation: scenarioInvalidation },
  // Reallocation mutations update their own cache via setQueryData, so only report data is invalidated
  { key: reallocationQueryKeys.all, invalidation: withReports() },
  // Subsidiary mutations apply the response via setQueryData for an instant update; the refetch syncs server-computed fields
  { key: companyGroupQueryKeys.all, invalidation: withReports(companyGroupQueryKeys.all) }
]

// Novu notification object type → entity key whose background recalculation finished
const novuEvents: Record<string, QueryKey> = {
  // BudgetingScenariosUpdated
  BudgetingScenario: budgetingScenarioQueryKeys.all
}

const dependencyMap = new Map(dependencies.map(d => [hashKey(d.key), d.invalidation]))

export const findInvalidation = (key: QueryKey | undefined): CacheInvalidation | undefined => {
  if (!key) return undefined
  return dependencyMap.get(hashKey(key))
}

export const findNovuInvalidation = (objectType: string | null): CacheInvalidation | undefined => {
  if (!objectType) return undefined
  return findInvalidation(novuEvents[objectType])
}

type ApplyInvalidationOptions = {
  // true: active report queries refetch immediately (mutation finished synchronously, 200)
  // false: reports are only marked stale; after a Novu event the user refetches them
  //        from the notification popup instead of the report changing under them
  refetchReports: boolean
  // false joins fetches already in flight instead of restarting them
  cancelRefetch?: boolean
}

export const applyInvalidation = (
  queryClient: QueryClient,
  { refetch, reports }: CacheInvalidation,
  { refetchReports, cancelRefetch = true }: ApplyInvalidationOptions
) =>
  Promise.all([
    ...refetch.map(queryKey => queryClient.invalidateQueries({ queryKey }, { cancelRefetch })),
    ...reports.map(queryKey =>
      queryClient.invalidateQueries({ queryKey, refetchType: refetchReports ? 'active' : 'none' }, { cancelRefetch })
    )
  ])
